import { useState, useRef, useEffect } from 'react';
import { api } from '../api/client';
import { useToast } from '../hooks/useToast';

const SIZE_OPTIONS = [
  { value: 'auto', label: '自动' },
  { value: '1:1', label: '1:1 正方形' },
  { value: '16:9', label: '16:9 横图' },
  { value: '9:16', label: '9:16 竖图' },
  { value: '4:3', label: '4:3 横图' },
  { value: '3:4', label: '3:4 竖图' },
  { value: '3:2', label: '3:2 横图' },
  { value: '2:3', label: '2:3 竖图' },
  { value: '5:4', label: '5:4 横图' },
  { value: '4:5', label: '4:5 竖图' },
  { value: '2:1', label: '2:1 横图' },
  { value: '1:2', label: '1:2 竖图' },
  { value: '3:1', label: '3:1 横图' },
  { value: '1:3', label: '1:3 竖图' },
  { value: '21:9', label: '21:9 横图' },
  { value: '9:21', label: '9:21 竖图' },
];

const RESOLUTION_OPTIONS = [
  { value: '1k', label: '1K (标清)' },
  { value: '2k', label: '2K (高清)' },
  { value: '4k', label: '4K (超清)' },
];

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 120000;

export default function KnowledgeVizPage() {
  const { showToast } = useToast();

  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('16:9');
  const [resolution, setResolution] = useState('2k');
  const [generating, setGenerating] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pollTimerRef = useRef<number | null>(null);
  const pollStartRef = useRef<number>(0);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const handleGenerate = async () => {
    if (generating) return;

    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) {
      showToast('请输入图像描述', 'warning');
      return;
    }

    setGenerating(true);
    setError(null);
    setImageUrl(null);
    setStatus('submitting');
    setProgress(0);

    try {
      const res = await api.generateImage({ prompt: trimmedPrompt, size, resolution });
      startPolling(res.task_id);
    } catch (e: any) {
      setGenerating(false);
      setError(e.message || '提交失败');
      setStatus(null);
    }
  };

  const startPolling = (taskId: string) => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollStartRef.current = Date.now();
    setStatus('processing');
    setProgress(10);

    const poll = async () => {
      const elapsed = Date.now() - pollStartRef.current;
      if (elapsed >= POLL_TIMEOUT_MS) {
        setGenerating(false);
        setError('生成超时，请稍后重试');
        setStatus(null);
        return;
      }

      try {
        const statusRes = await api.getImageTaskStatus(taskId);

        if (statusRes.status === 'completed' && statusRes.image_url) {
          setGenerating(false);
          setStatus('completed');
          setProgress(100);
          setImageUrl(statusRes.image_url);
          return;
        }

        if (statusRes.status === 'failed') {
          setGenerating(false);
          setError(statusRes.error || '生成失败');
          setStatus(null);
          return;
        }

        setProgress(statusRes.progress || 10);
        setStatus(statusRes.status);
        pollTimerRef.current = window.setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e: any) {
        setGenerating(false);
        setError(e.message || '查询失败');
        setStatus(null);
      }
    };

    pollTimerRef.current = window.setTimeout(poll, POLL_INTERVAL_MS);
  };

  return (
    <div className="page-container knowledge-viz-page">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="glass-card hero-card mb-4">
            <div className="hero-eyebrow text-fg-muted mb-1">知识可视化</div>
            <h2 className="mb-2">AI 图像生成</h2>
            <p className="text-fg-muted mb-0">用一句话描述你的学术概念或知识内容，AI 将为你生成可视化图像</p>
          </div>

          <div className="glass-card mb-4">
            <div className="mb-3">
              <label className="form-label text-fg-muted" htmlFor="vizPrompt">描述你想生成的知识图像</label>
              <textarea
                className="form-control viz-prompt-input"
                id="vizPrompt"
                rows={4}
                placeholder="例如：黑洞吸积盘的艺术化示意图，展示物质螺旋坠落和相对论喷流，科幻科普风格"
                maxLength={2000}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
              <div className="text-end mt-1">
                <small className="text-fg-muted">{prompt.length} / 2000</small>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6 mb-3 mb-md-0">
                <label className="form-label text-fg-muted" htmlFor="vizSize">图片比例</label>
                <select className="form-select" id="vizSize" value={size} onChange={(e) => setSize(e.target.value)}>
                  {SIZE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label text-fg-muted" htmlFor="vizResolution">分辨率</label>
                <select className="form-select" id="vizResolution" value={resolution} onChange={(e) => setResolution(e.target.value)}>
                  {RESOLUTION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <button className="btn btn-primary w-100" type="button" disabled={generating} onClick={handleGenerate}>
              {generating ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>生成中...</>
              ) : (
                <><i className="fas fa-wand-magic-sparkles me-2"></i>生成图像</>
              )}
            </button>
          </div>

          {(status || error || imageUrl) && (
            <div className="glass-card mb-4">
              {error && (
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-circle me-2"></i>{error}
                </div>
              )}

              {status && status !== 'completed' && !error && (
                <div className="text-center py-4">
                  <div className="progress mb-3" style={{ height: 8 }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      style={{ width: `${Math.min(progress, 95)}%` }}
                    />
                  </div>
                  <p className="text-fg-muted mb-0">
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {status === 'processing' ? '生成中...' : `处理中... (${progress}%)`}
                  </p>
                </div>
              )}

              {imageUrl && (
                <>
                  <div className="text-center mb-3">
                    <span className="badge bg-success">
                      <i className="fas fa-check-circle me-1"></i>生成完成
                    </span>
                  </div>
                  <div className="text-center">
                    <img
                      src={imageUrl}
                      className="img-fluid rounded generated-image"
                      alt="生成的图像"
                      style={{ maxHeight: 600 }}
                    />
                    <div className="mt-3">
                      <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline-light btn-sm me-2">
                        <i className="fas fa-external-link-alt me-1"></i>新窗口查看
                      </a>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}